<script setup lang="ts">
import { arrayToMap } from "@/api/query";
import { useSites } from "@/api/site-query";
import { useSpaces } from "@/api/space-query";
import AppDataTable from "@/components/common/AppDataTable.vue";
import ColumnName from "@/components/common/ColumnName.vue";
import DesktopOnly from "@/components/common/DesktopOnly.vue";
import Frame from "@/components/common/Frame.vue";
import CreateSiteDialog from "@/components/dialogs/CreateSiteDialog.vue";
import { icPlaceOutline } from "@/components/icons";
import DashboardNavButton from "@/components/navigation/DashboardNavButton.vue";
import IntegrityNavButton from "@/components/navigation/IntegrityNavButton.vue";
import MapNavButton from "@/components/navigation/MapNavButton.vue";
import SettingNavButton from "@/components/navigation/SettingNavButton.vue";
import FeatureGuard from "@/components/permission/FeatureGuard.vue";
import PermissionGuard from "@/components/permission/PermissionGuard.vue";
import ScopeGuard from "@/components/permission/ScopeGuard.vue";
import StationLinkedBadge from "@/components/stations/StationLinkedBadge.vue";
import NavBarStatus from "@/components/workspace/NavBarStatus.vue";
import { useFavoriteSiteSelection } from "@/composable/favorite-selection";
import Drawer from "@/layouts/drawer/Drawer.vue";
import DrawerLayout from "@/layouts/drawer/DrawerLayout.vue";
import { DrawerSelection } from "@/layouts/drawer/use-drawer-data";
import {
	StationStatusStateExtended,
	useDrawerState,
	useDrawerStore,
	useLiveStatusStore,
} from "@/stores";
import { StationSummary } from "@soft-db/monitoring-api-client";
import {
	Button,
	ColumnSelection,
	Icon,
	LinkButton,
	makeColumnHelper,
	makeFacetedMapping,
} from "@soft-db/ui";
import { storeToRefs } from "pinia";

type SiteRowData = {
	id: string;
	name: string;
	description: string;
	station: StationSummary | null;
	status: StationStatusStateExtended[];
	connected: boolean;
	spaces: { name: string; id: string }[];
	inFavorite: boolean;
};

const liveStatusStore = useLiveStatusStore();

const { toggleDrawer } = useDrawerStore();

const { t } = useI18n();

const router = useRouter();
const route = useRoute();

function changePage(selection: DrawerSelection) {
	router.push({
		name: "sites-id-settings",
		params: { id: selection.id },
		query: { ...route.query },
	});
	toggleDrawer();
}

const { sites, isLoading: sitesLoading } = useSites();
const { spaces, isLoading: spacesLoading } = useSpaces();

const spacesMap = computed(() => arrayToMap(spaces.value || []));

const selection = useFavoriteSiteSelection(sites);

const isLoading = logicOr(sitesLoading, spacesLoading);

const createSiteDialog = ref(false);
function openCreateSiteDialog() {
	createSiteDialog.value = true;
}

function goToSite(id: string) {
	router.push({ name: "sites-id-settings", params: { id: id } });
}

const siteRows = computed<SiteRowData[]>(() => {
	if (isLoading.value || !sites.value) return [];

	return sites.value.map((site) => {
		return {
			id: site.id,
			name: site.name,
			description: site.description,
			station: site.station,
			status: liveStatusStore.searchableStatusState("site", site.id),
			connected: site.station != null,
			spaces:
				site.spaces.length === 0
					? // We force one item with an empty string, so we can filter on the `None` relation.
						[{ id: "", name: "" }]
					: site.spaces.map((id) => ({ id, name: spacesMap.value.get(id)?.name || "" })),
			inFavorite: site.inFavorite,
		};
	});
});

const filtersDef = computed(() => {
	return makeFacetedMapping<SiteRowData>({
		status: {
			name: t("common.status"),
			allowMultiple: false,
			choices: {
				online: {
					name: t("live-status.online"),
				},
				waiting: {
					name: t("live-status.idle"),
				},
				"recently-offline": {
					name: t("live-status.recentlyOffline"),
					hint: t("live-status.recentlyOfflineHelp"),
				},
				offline: {
					name: t("live-status.offline"),
				},
				none: {
					name: t("common.notLinked"),
				},
			},
		},

		connected: {
			name: t("common.linked"),
			allowMultiple: false,
			hint: t("common.linkedHelp"),
			choices: {
				true: {
					name: t("common.linked"),
				},
				false: {
					name: t("common.notLinked"),
				},
			},
		},

		inFavorite: {
			name: t("common.favorite"),
			allowMultiple: false,
			choices: {
				true: {
					name: t("common.inFavorite"),
				},
				false: {
					name: t("common.notInFavorite"),
				},
			},
		},

		spaces: {
			name: t("object.space"),
			allowMultiple: true,
			allowSearch: true,
			choices: { "": { name: t("space.noSpace") } },
		},
	});
});

const drawerState = useDrawerState();
const { filter } = storeToRefs(drawerState);

const fieldFilters = drawerState.getFilters(Object.keys(filtersDef.value));

const { Column } = makeColumnHelper<SiteRowData>();
</script>

<template>
	<DrawerLayout mobileOnly>
		<template #drawer>
			<Drawer :objects="['site']" @click="changePage" />
		</template>
		<Frame class="px-0!" topMargin>
			<AppDataTable
				v-model:filter="filter"
				v-model:fieldFilters="fieldFilters"
				:data="siteRows"
				:filtersDef="filtersDef"
				:loading="isLoading"
				stateStorage="url"
			>
				<template #title>
					<h1 class="flex items-center gap-1 text-center capitalize">
						<Icon :d="icPlaceOutline" />
						{{ t("object.project", 2) }}
					</h1>
				</template>

				<template #action>
					<PermissionGuard permission="site.create">
						<Button size="sm" variant="outline" @click="openCreateSiteDialog">
							{{ t("actions.createSite") }}
						</Button>
					</PermissionGuard>
				</template>

				<template #columns>
					<FeatureGuard feature="favorite">
						<ColumnSelection v-model:selection="selection" selectionMode="multiple" />
					</FeatureGuard>

					<Column
						v-slot="{ row: { original: site } }"
						field="name"
						:header="t('common.name')"
						enableSorting
						enableGlobalFilter
						mobileMain
					>
						<ColumnName :id="site.id" :name="site.name" :description="site.description" />
					</Column>

					<DesktopOnly>
						<ScopeGuard scope="distributor">
							<Column
								id="spaces"
								v-slot="{ row: { original: site } }"
								:fieldFn="(site) => site.spaces.map((space) => space.name)"
								:header="t('object.space')"
								enableColumnFilter
							>
								<div class="flex flex-wrap gap-1">
									<template v-for="space of site.spaces">
										<LinkButton
											v-if="space.id"
											:key="space.id"
											color="primary"
											variant="soft"
											size="xs"
											:to="{ name: 'spaces-id', params: { id: space.id } }"
										>
											{{ space.name }}
										</LinkButton>
									</template>
								</div>
							</Column>
						</ScopeGuard>
					</DesktopOnly>

					<PermissionGuard permission="station.get">
						<Column
							id="station"
							v-slot="{ row: { original: site } }"
							:fieldFn="(d) => d.station?.name || ''"
							:header="t('object.station', 1)"
							enableSorting
							enableGlobalFilter
						>
							<StationLinkedBadge :station="site.station" />
						</Column>
					</PermissionGuard>

					<Column
						v-slot="{ row: { original: site } }"
						field="status"
						enableColumnFilter
						header=""
						shrink
					>
						<NavBarStatus class="float-right" sourceType="site" :sourceId="site.id" />
					</Column>

					<Column v-slot="{ row: { original: site } }" shrink>
						<div class="flex gap-1">
							<MapNavButton
								sourceType="site"
								:sourceId="site.id"
								:inFavorite="site.inFavorite"
								size="sm"
							/>
							<DashboardNavButton :siteId="site.id" size="sm" />
							<IntegrityNavButton sourceType="site" :sourceId="site.id" size="sm" />
							<SettingNavButton sourceType="site" :sourceId="site.id" size="sm" />
						</div>
					</Column>

					<!-- Hidden columns, added for search -->
					<Column hidden field="id" enableGlobalFilter />
					<Column hidden field="description" enableGlobalFilter />
					<PermissionGuard permission="station.get">
						<Column hidden field="connected" enableColumnFilter />
					</PermissionGuard>

					<FeatureGuard feature="favorite">
						<Column hidden field="inFavorite" enableColumnFilter />
					</FeatureGuard>
				</template>
			</AppDataTable>
		</Frame>
		<CreateSiteDialog v-model:dialog="createSiteDialog" @created="goToSite" />
	</DrawerLayout>
</template>
